// Copyright (c) 2022 by Move Closer
import { ComponentInternalInstance, computed, PropType, toRefs, Ref } from '@vue/composition-api'

import { canBeDisabledProp, hasErrorsProp } from '../../composables'
import { ComponentObjectPropsOptions, Emit } from '../../vue-api'
import { HTMLInputEvent } from '../../abstract/general'

import { FileSelectProps, UseFileSelectProvides } from './FileSelect.contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const fileSelectProps: ComponentObjectPropsOptions<FileSelectProps> = {
  ...canBeDisabledProp,
  ...hasErrorsProp,

  files: {
    type: Array as PropType<File[]>,
    required: true
  },

  fileTypes: {
    type: Array,
    required: false,
    default: () => (['*'])
  },

  isMultiple: {
    type: Boolean,
    required: false,
    default: false
  },

  isRequired: {
    type: Boolean,
    required: false,
    default: false
  },

  label: {
    type: String,
    required: false,
    default: ''
  },

  maxFileSize: {
    type: Number,
    required: false,
    default: 3
  }
}

/**
 *
 * Handles adding to particular list of files , the file which is selected
 *
 * @param cInstance - Component's instance (required to use i18n)
 * @param event - Event where we reach to target.files.
 * @param files - List of files where each file will be added.
 * @param maxFileSize - Maximum file size that can be selected.
 *
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
const addToFiles = (event: HTMLInputEvent, files: Ref<File[]>, cInstance: ComponentInternalInstance, maxFileSize: number = 2): void => {
  if (!event.target || !event.target.files) {
    return
  }

  const maxFileSizeInBytes = (maxFileSize * 1048576)

  for (let i = 0; i < event.target.files.length; i++) {
    const item = event.target.files[i]

    if (typeof item === 'undefined') {
      continue
    }

    if (maxFileSizeInBytes < item.size) {
      throw new Error(String(cInstance.proxy.$t('dsl.fileSelect.exceptions.fileLimitSize', { maxFileSize: maxFileSize, givenSize: Math.ceil(item.size / 1048576) })))
      // throw new Error(`Can not select files that has more than allowed size! Max file size: ${maxFileSize}MB, got ${Math.ceil(item.size / 1048576)}MB`)
    }

    files.value.push(item)
  }
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const useFileSelect = (
  props: FileSelectProps,
  emit: Emit,
  internalInstance: ComponentInternalInstance | null): UseFileSelectProvides => {
  if (!internalInstance) {
    throw new Error('useFileSelect(): Instance for component is not found!')
  }

  const { maxFileSize, files } = toRefs(props)

  /**
   * @see UseFileSelectProvides._label
   */
  const _label = computed<string>(() => {
    if (typeof props.label === 'undefined' || !props.label.length) {
      return String(internalInstance.proxy.$t('dsl.fileSelect.label'))
    }

    return props.label
  })

  /**
   * @see UseFileSelectProvides.hsaFiles
   */
  const hasFiles = computed<boolean>(() => {
    return Array.isArray(files.value) && files.value.length > 0
  })

  /**
   * @see UseFileSelectProvides.selectItem
   */
  const removeItem = (item: File): void => {
    if (files.value.indexOf(item) === -1) {
      return
    }

    const sortedFiles = files.value.filter((file: File) => file !== item)

    emit('update:files', sortedFiles)
  }

  /**
   * @see UseFileSelectProvides.selectItem
   */
  const selectItem = (event: HTMLInputEvent): void => {
    try {
      addToFiles(event, files, internalInstance, maxFileSize.value)

      // Clean errors after file is "successfully" selected.
      emit('update:errors', [])
    } catch (e: any) {
      emit('update:errors', [e.message])
    }
  }

  return { _label, hasFiles, removeItem, selectItem }
}
